import React from "react";
import { Link } from "react-router-dom";
import { GraduationCap, ArrowRight } from "lucide-react";
import { courseTotals } from "../../data/course";
import { hasCourseAccess } from "./access";

interface CourseCardProps {
  customerTier: string;
}

/**
 * The course, as a tile in the Content Library grid.
 *
 * It sits with the book downloads because that is where a customer looks for
 * what they own; as a full-width strip below the grid it read as chrome and
 * got missed. It deliberately does NOT pretend to be a file — the decks are
 * sixteen PDFs behind their own page — so the action opens /course instead of
 * downloading, and the meta line carries a deck count where a download card
 * carries a version.
 *
 * Renders nothing without the entitlement: the course is not advertised while
 * the decks are still being revised.
 */
const CourseCard: React.FC<CourseCardProps> = ({ customerTier }) => {
  if (!hasCourseAccess(customerTier)) return null;

  return (
    <div className="group border border-tertiary rounded-lg p-3 sm:p-4 hover:border-accent/30 transition-all duration-200 bg-secondary/20 hover:bg-secondary/40 flex flex-col">
      <div className="flex items-start space-x-3 sm:space-x-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-16 sm:w-16 sm:h-20 bg-gradient-to-br from-accent to-accent/70 rounded-lg flex items-center justify-center text-white">
            <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-primary text-base sm:text-lg leading-snug">
            The Course
          </h3>
          <p className="text-secondary text-sm mt-0.5">
            Every chapter as presentation slides
          </p>
          <p className="text-secondary text-xs mt-2">
            {courseTotals.decks} chapter decks · {courseTotals.slides} slides
          </p>
        </div>
      </div>

      <Link
        to="/course"
        className="mt-3 sm:mt-4 inline-flex items-center justify-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent/90"
      >
        Open the course
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
};

export default CourseCard;
